import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { fmtDay, t12 } from "@/lib/daytime";

const PST = [["on_way", "ON MY WAY", "bg-turquoise text-navy"], ["here", "I'M HERE", "bg-emerald-500 text-white"], ["late", "RUNNING LATE", "bg-coral text-white"]];

function UpcomingRow({ b, days, cutoff, onChange, onPay }) {
  const [moving, setMoving] = useState(false);
  const [to, setTo] = useState("");
  const opts = days.filter((d) => (d.status === "available" || d.status === "limited") && d.date !== b.date);
  const cancel = async () => {
    if (!window.confirm(`Cancel ${b.child_name}'s play day on ${fmtDay(b.date)}? Cancelling less than ${cutoff} hours before may use the visit.`)) return;
    try { const { data } = await api.post(`/daytime/bookings/${b.id}/cancel`); toast.success(data.message || "Cancelled"); onChange(); } catch (e) { toast.error(apiErr(e)); }
  };
  const move = async () => {
    if (!to) return toast.error("Pick a new date");
    try { await api.post(`/daytime/bookings/${b.id}/reschedule`, { date: to }); toast.success("Rescheduled!"); setMoving(false); onChange(); } catch (e) { toast.error(apiErr(e)); }
  };
  const status = async (s) => {
    try { await api.post(`/daytime/bookings/${b.id}/status`, { status: s }); toast.success("Thanks — our team can see your update"); onChange(); } catch (e) { toast.error(apiErr(e)); }
  };
  const today = b.date === new Date().toLocaleDateString("en-CA", { timeZone: "Atlantic/Bermuda" });
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid={`upcoming-${b.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div><p className="font-bold text-navy">{fmtDay(b.date, { weekday: "long", month: "short", day: "numeric" })}{b.start ? ` · ${t12(b.start)}–${t12(b.end)}` : ""}</p>
          <p className="text-xs text-slate-500">{b.child_name} · {b.type_label}{b.reference ? ` · Ref ${b.reference}` : ""}</p></div>
        {!b.paid && (b.order?.status === "awaiting_confirmation"
          ? <Badge className="bg-amber-100 text-amber-800" data-testid={`awaiting-${b.id}`}>Payment sent · awaiting confirmation</Badge>
          : b.order?.booking_confirmed_at
            ? <Badge className="bg-emerald-100 text-emerald-800" data-testid={`confirmed-due-${b.id}`}>Booking confirmed · payment due</Badge>
            : <Badge className="bg-sky-100 text-sky-800" data-testid={`received-${b.id}`}>Booking received · payment due</Badge>)}
      </div>
      {!b.paid && b.order && b.order.status === "pending" && (
        <button onClick={() => onPay(b.order)} className="mt-2 w-full rounded-xl bg-gold text-navy font-black text-sm py-3 hover:bg-gold/90 transition-colors" data-testid={`pay-booking-${b.id}`}>
          MAKE PAYMENT WHEN YOU'RE READY · ${Number(b.order.amount).toFixed(2)}
        </button>
      )}
      {today && <div className="grid grid-cols-3 gap-1.5 mt-2" data-testid={`parent-status-${b.id}`}>
        {PST.map(([k, l, c]) => <button key={k} onClick={() => status(k)} className={`rounded-xl py-2.5 text-[11px] font-black ${c} ${b.parent_status === k ? "ring-2 ring-navy" : "opacity-90"}`} data-testid={`pstatus-${k}-${b.id}`}>{l}</button>)}
      </div>}
      <div className="flex flex-wrap gap-2 mt-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setMoving(!moving)} data-testid={`reschedule-${b.id}`}>Reschedule</Button>
        <Button size="sm" variant="outline" className="rounded-full text-coral border-coral/40" onClick={cancel} data-testid={`cancel-${b.id}`}>Cancel</Button>
      </div>
      {moving && (
        <div className="flex gap-2 mt-2">
          <select value={to} onChange={(e) => setTo(e.target.value)} className="flex-1 border rounded-xl px-2 text-sm h-10" data-testid={`reschedule-select-${b.id}`}>
            <option value="">Choose a new day</option>
            {opts.map((d) => <option key={d.date} value={d.date}>{fmtDay(d.date)} · {d.spaces} left</option>)}
          </select>
          <Button size="sm" className="rounded-full bg-navy h-10" onClick={move} data-testid={`reschedule-confirm-${b.id}`}>Move</Button>
        </div>
      )}
    </div>
  );
}

export function MyPlayDays({ me, days, onChange, onPay }) {
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div data-testid="upcoming-play-days">
        <p className="font-display font-extrabold text-navy mb-2">UPCOMING PLAY DAYS</p>
        {me.upcoming.length === 0 ? <p className="text-sm text-slate-400">No play days booked yet.</p> :
          <div className="space-y-2">{me.upcoming.map((b) => <UpcomingRow key={b.id} b={b} days={days} cutoff={me.settings.cancel_cutoff_hours} onChange={onChange} onPay={onPay} />)}</div>}
        {me.waitlist.length > 0 && <div className="mt-4" data-testid="my-waitlist">
          <p className="font-bold text-navy text-sm mb-1">Waitlist</p>
          {me.waitlist.map((w) => <p key={w.id} className="text-sm text-slate-600">{fmtDay(w.date)} — {w.status === "offered" ? <b className="text-emerald-600">AVAILABLE TO BOOK</b> : "waiting"}</p>)}
        </div>}
      </div>
      <div data-testid="past-visits">
        <p className="font-display font-extrabold text-navy mb-2">PAST VISITS</p>
        {me.past.length === 0 ? <p className="text-sm text-slate-400">No visits yet.</p> :
          <div className="space-y-1.5">{me.past.map((b) => (
            <div key={b.id} className="flex justify-between text-sm bg-white border border-slate-100 rounded-xl px-3 py-2">
              <span>{fmtDay(b.date)} · {b.child_name}</span><span className="text-slate-400 capitalize">{b.status.replace("_", " ")}</span>
            </div>))}</div>}
      </div>
    </div>
  );
}
